import React, { useEffect, useMemo, useState } from 'react'
import { useGetMyInfo } from 'app/gomsacademy/hooks/GetMyInfo'
import { useAcademyList } from 'app/gomsacademy/hooks/AcademyList'
import * as antd from 'antd'
import styled from 'styled-components'
import API from 'utils/api'
import { useToken } from 'app/hooks'

const MasterBulkSMS = () => {
	const { data: token } = useToken()
	const { myInfo } = useGetMyInfo()
	const { AcademyListData } = useAcademyList()

	const [message, setMessage] = useState('')
	const [title, setTitle] = useState('')
	const [academySearch, setAcademySearch] = useState('')
	const [selectedAcademies, setSelectedAcademies] = useState<number[]>([])
	const [loading, setLoading] = useState(false)
	const [result, setResult] = useState<any>(null)

	// history state
	const [historyLoading, setHistoryLoading] = useState(false)
	const [historyList, setHistoryList] = useState<any[]>([])
	const [historyPage, setHistoryPage] = useState(1)
	const [historyPageSize, setHistoryPageSize] = useState(20)
	const [historyTotal, setHistoryTotal] = useState(0)
	const [historyFilters, setHistoryFilters] = useState<{
		startDate?: string
		endDate?: string
		phone?: string
		deptId?: number | undefined
	}>({})

	// API에서 master가 1/0 숫자로 올 수 있으므로 truthy로 판별
	const isMaster = !!myInfo?.master

	const [tab, setTab] = useState<'send' | 'history'>('send')

	const fetchHistory = async (page = 1, pageSize = historyPageSize, extraFilters: any = {}) => {
		if (!token) return

		setHistoryLoading(true)
		try {
			const params: any = {
				page,
				maxData: pageSize,
				...historyFilters,
				...extraFilters,
			}

			const searchParams = new URLSearchParams()
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null && value !== '') {
					searchParams.append(key, String(value))
				}
			})

			const queryString = searchParams.toString()
			const url = `/message/master/history${queryString ? `?${queryString}` : ''}`

			const res = await API.get(url, token + '', {})
			if (res.code === 0) {
				setHistoryList(res.result?.list || [])
				setHistoryTotal(res.result?.totalCount || 0)
				setHistoryPage(res.result?.currentPage || page)
				setHistoryPageSize(pageSize)
			} else {
				throw new Error(res.message || '발송 내역 조회에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '발송 내역 조회 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
		} finally {
			setHistoryLoading(false)
		}
	}

	useEffect(() => {
		if (isMaster) {
			fetchHistory(1, historyPageSize)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMaster])

	const handleSend = async () => {
		if (!message.trim()) {
			antd.message.error('메시지 내용을 입력해주세요.')
			return
		}

		const targetDeptIds = selectedAcademies.length > 0 ? selectedAcademies : undefined

		setLoading(true)
		try {
			const res = await API.post('/message/master/bulk-sms', token + '', {
				message: message.trim(),
				deptIds: targetDeptIds,
			})

			if (res.result?.success) {
				antd.message.success('문자 발송이 완료되었습니다.')
				setResult(res.result)
				setMessage('')
				setSelectedAcademies([])
				// refresh history after successful send
				fetchHistory(1, historyPageSize)
			} else {
				throw new Error(res.result?.message || '발송에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '문자 발송 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
		} finally {
			setLoading(false)
		}
	}

	const handleSelectAll = () => {
		if (selectedAcademies.length === AcademyListData?.length) {
			setSelectedAcademies([])
		} else {
			setSelectedAcademies(AcademyListData?.map((a: any) => a.id) || [])
		}
	}

	const filteredAcademies = useMemo(() => {
		const list = Array.isArray(AcademyListData) ? AcademyListData : []
		const keyword = academySearch.trim().toLowerCase()
		if (!keyword) return list
		return list.filter((a: any) => String(a?.name || '').toLowerCase().includes(keyword))
	}, [AcademyListData, academySearch])

	const toggleAllFiltered = () => {
		const ids = filteredAcademies.map((a: any) => Number(a.id)).filter((v: number) => !Number.isNaN(v))
		const allSelected = ids.length > 0 && ids.every((id: number) => selectedAcademies.includes(id))
		setSelectedAcademies(
			allSelected
				? selectedAcademies.filter((id) => !ids.includes(id))
				: Array.from(new Set([...selectedAcademies, ...ids]))
		)
	}

	const toggleOne = (id: number) => {
		setSelectedAcademies((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
	}

	// 미리보기용: 선택된 학원 중 첫 번째, 없으면 필터 목록 첫 번째
	const previewAcademy = useMemo(() => {
		if (selectedAcademies.length > 0) {
			const firstId = selectedAcademies[0]
			return filteredAcademies.find((a: any) => Number(a.id) === Number(firstId))
		}
		return filteredAcademies[0]
	}, [selectedAcademies, filteredAcademies])

	const previewMessageText = useMemo(() => {
		if (!message) return ''
		const academyName = String(previewAcademy?.name || '')
		const adminName = String(previewAcademy?.representativeName || '')
		let out = message
		out = out.replace(/#\{학원이름\}/g, academyName)
		out = out.replace(/#\{학원명\}/g, academyName)
		out = out.replace(/#\{원장이름\}/g, adminName)
		out = out.replace(/#\{원장명\}/g, adminName)
		return out
	}, [message, previewAcademy])

	if (!isMaster) {
		return (
			<Container>
				<antd.Card>
					<p>마스터 관리자만 접근 가능합니다.</p>
				</antd.Card>
			</Container>
		)
	}

	const historyColumns: antd.TableProps<any>['columns'] = [
		{
			title: '발송일시',
			dataIndex: 'createdAt',
			key: 'createdAt',
		},
		{
			title: '학원명',
			dataIndex: 'academyName',
			key: 'academyName',
		},
		{
			title: '원장명',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: '수신번호',
			dataIndex: 'phone',
			key: 'phone',
		},
		{
			title: '상태',
			dataIndex: 'status',
			key: 'status',
			render: (value: boolean) => (value ? '성공' : '실패'),
		},
		{
			title: '실패 사유',
			dataIndex: 'errorMessage',
			key: 'errorMessage',
		},
	]

	return (
		<Container>
			<Header>
				<Title>메시지</Title>
				<TabsBar>
					<TabButton active={tab === 'send'} onClick={() => setTab('send')}>
						문자 보내기
					</TabButton>
					<TabButton active={tab === 'history'} onClick={() => setTab('history')}>
						발송 내역
					</TabButton>
				</TabsBar>
			</Header>

			{tab === 'send' && (
				<>
					<Row>
						<Card>
							<CardTitle>수신자 선택</CardTitle>
							<Controls>
								<SearchInput
									value={academySearch}
									onChange={(e) => setAcademySearch(e.target.value)}
									placeholder="학원명 검색"
								/>
								<SecondaryButton onClick={toggleAllFiltered}>필터 전체 선택/해제</SecondaryButton>
							</Controls>
							<List>
								{filteredAcademies.map((academy: any) => {
									const id = Number(academy.id)
									const checked = selectedAcademies.includes(id)
									const phone = academy.tel ? String(academy.tel).trim() : ''
									const repName = academy.representativeName ? String(academy.representativeName).trim() : ''
									return (
										<ListRow key={id}>
											<input type="checkbox" checked={checked} onChange={() => toggleOne(id)} />
											<div>
												<div>
													<Name>{academy.name}</Name>
												</div>
												<Meta>
													{repName ? `원장: ${repName}` : ''}
													{' / '}
													{phone ? `연락처: ${phone}` : '연락처: -'}
												</Meta>
											</div>
										</ListRow>
									)
								})}
							</List>
							<Footer>
								<div>선택: {selectedAcademies.length}개 학원</div>
								<SecondaryButton onClick={() => setSelectedAcademies([])}>선택 초기화</SecondaryButton>
							</Footer>
						</Card>

						<Card>
							<CardTitle>전송 설정</CardTitle>
							<div style={{ marginBottom: 8, fontWeight: 700 }}>
								제목 (선택) - 제목을 입력하면 LMS로 발송됩니다
							</div>
							<SearchInput
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="예: 공지사항, 안내사항 등"
							/>
							<div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
								메시지 내용 (필수)
								<span style={{ marginLeft: 8, fontWeight: 600, color: '#666', fontSize: 12 }}>
									사용 가능: {'#{학원이름}'}, {'#{원장이름}'}
								</span>
							</div>
							<MessageTextArea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder="메시지 내용을 입력하세요. #{학원이름}, #{원장이름} 등의 플레이스홀더를 사용할 수 있습니다."
								rows={8}
							/>
							<div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
								메시지 내용 미리보기
							</div>
							<PreviewBox>
								{!message
									? '메시지 내용을 입력하면 미리보기가 표시됩니다.'
									: previewMessageText || message}
							</PreviewBox>
							<PreviewMeta>
								미리보기 대상:{' '}
								{previewAcademy
									? `${previewAcademy.name}${previewAcademy.representativeName ? ` (원장: ${previewAcademy.representativeName})` : ''}`
									: `선택 ${selectedAcademies.length}개 학원`}
							</PreviewMeta>
							<Footer>
								<div />
								<SendButton onClick={handleSend} disabled={loading || !message.trim()}>
									{loading ? '전송 중...' : '단체 SMS 전송'}
								</SendButton>
							</Footer>
						</Card>
					</Row>

					{result?.results && (
						<ResultTable>
							<thead>
								<tr>
									<th>학원명</th>
									<th>원장명</th>
									<th>전화번호</th>
									<th>결과</th>
								</tr>
							</thead>
							<tbody>
								{result.results.map((r: any, idx: number) => (
									<tr key={idx}>
										<td>{r.academyName}</td>
										<td>{r.name}</td>
										<td>{r.phone || '-'}</td>
										<td>
											<Badge ok={!!r.success}>{r.success ? '성공' : '실패'}</Badge>
											{r.reason ? ` (${r.reason})` : ''}
										</td>
									</tr>
								))}
							</tbody>
						</ResultTable>
					)}
				</>
			)}

			{tab === 'history' && (
				<FormSection>
					<FormRow>
						<FormLabel>조회 조건</FormLabel>
						<FilterRow>
							<antd.DatePicker.RangePicker
								onChange={(dates, dateStrings) => {
									const [start, end] = dateStrings as [string, string]
									const nextFilters = {
										...historyFilters,
										startDate: start || undefined,
										endDate: end || undefined,
									}
									setHistoryFilters(nextFilters)
								}}
							/>
							<antd.Input
								placeholder="수신 번호"
								style={{ width: 180 }}
								onChange={(e) => {
									const nextFilters = {
										...historyFilters,
										phone: e.target.value || undefined,
									}
									setHistoryFilters(nextFilters)
								}}
							/>
							<antd.Select
								placeholder="학원 선택"
								allowClear
								style={{ width: 200 }}
								onChange={(value) => {
									const nextFilters = {
										...historyFilters,
										deptId: value || undefined,
									}
									setHistoryFilters(nextFilters)
								}}
							>
								{AcademyListData?.map((academy: any) => (
									<antd.Select.Option key={academy.id} value={academy.id}>
										{academy.name}
									</antd.Select.Option>
								))}
							</antd.Select>
							<antd.Button
								type="primary"
								onClick={() => {
									fetchHistory(1, historyPageSize, {})
								}}
							>
								검색
							</antd.Button>
						</FilterRow>
					</FormRow>

					<antd.Table
						rowKey="id"
						loading={historyLoading}
						columns={historyColumns}
						dataSource={historyList}
						pagination={{
							current: historyPage,
							pageSize: historyPageSize,
							total: historyTotal,
							showSizeChanger: true,
							onChange: (page, pageSize) => {
								fetchHistory(page, pageSize, {})
							},
						}}
					/>
				</FormSection>
			)}
		</Container>
	)
}

const Container = styled.div`
	padding: 24px;
	width: 100%;
`

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
`

const Title = styled.h2`
	font-size: 24px;
	font-weight: 700;
	color: #111111;
	margin: 0;
`

const TabsBar = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
`

const TabButton = styled.button<{ active: boolean }>`
	padding: 8px 12px;
	border-radius: 8px;
	border: 1px solid ${(p) => (p.active ? '#f78b85' : '#e4e1e1')};
	background-color: ${(p) => (p.active ? '#f78b85' : '#fff')};
	color: ${(p) => (p.active ? '#fff' : '#111')};
	cursor: pointer;
	font-weight: 600;
	&:hover {
		background-color: ${(p) => (p.active ? '#f78b85' : '#fafafa')};
	}
`

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`

const Card = styled.div`
	border: 1px solid #f6f1f1;
	border-radius: 12px;
	background: #fff;
	padding: 16px;
`

const CardTitle = styled.div`
	font-size: 16px;
	font-weight: 700;
	margin-bottom: 12px;
	color: #111;
`

const Controls = styled.div`
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 12px;
`

const SearchInput = styled.input`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	flex: 1;
	min-width: 180px;
`

const List = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	overflow: hidden;
	max-height: 420px;
	overflow-y: auto;
`

const ListRow = styled.label`
	display: flex;
	gap: 10px;
	align-items: center;
	padding: 10px 12px;
	border-bottom: 1px solid #f5f5f5;
	cursor: pointer;
	user-select: none;
	&:hover {
		background: #fafafa;
	}
	&:last-child {
		border-bottom: none;
	}
`

const Name = styled.span`
	font-weight: 600;
	color: #111;
`

const Meta = styled.span`
	color: #666;
	font-size: 12px;
`

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 12px;
	flex-wrap: wrap;
`

const SecondaryButton = styled.button`
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #e4e1e1;
	background: #fff;
	color: #111;
	font-weight: 700;
	cursor: pointer;
`

const SendButton = styled.button`
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #f78b85;
	background: #f78b85;
	color: #fff;
	font-weight: 700;
	cursor: pointer;
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`

const MessageTextArea = styled.textarea`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	width: 100%;
	min-height: 120px;
	resize: vertical;
	font-family: inherit;
`

const PreviewBox = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 10px;
	background: #fafafa;
	padding: 12px;
	white-space: pre-wrap;
	font-size: 13px;
	line-height: 1.5;
	color: #111;
	min-height: 100px;
`

const PreviewMeta = styled.div`
	margin-top: 8px;
	font-size: 12px;
	color: #666;
`

const ResultTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #f6f1f1;
	margin-top: 16px;
	th {
		padding: 10px;
		background-color: #fff5f5;
		border: 1px solid #f6f1f1;
		text-align: left;
		font-weight: 700;
		color: #333;
		font-size: 13px;
	}
	td {
		padding: 10px;
		border: 1px solid #f6f1f1;
		color: #111;
		font-size: 13px;
	}
`

const Badge = styled.span<{ ok: boolean }>`
	padding: 4px 8px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 700;
	background-color: ${(p) => (p.ok ? '#e6f7e6' : '#ffe6e6')};
	color: ${(p) => (p.ok ? '#52c41a' : '#ff4d4f')};
`

const FormSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`

const FormRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const FormLabel = styled.label`
	font-weight: 600;
	font-size: 14px;
	color: #333;
`

const FilterRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	align-items: center;
`

export default MasterBulkSMS
